import { useMsal, useAccount, useIsAuthenticated } from '@azure/msal-react';
import { BrowserAuthError, BrowserAuthErrorCodes, InteractionStatus } from '@azure/msal-browser';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { loginRequest } from '@/lib/msalConfig';
import { usersApi, usersKeys } from '@/lib/api/admin';
import type { ApiError, UserRole } from '@/types';

interface AuthUser {
  id: string;
  email: string;
  name: string;
  roles: UserRole[];
  /**
   * True when the backend `/users/me` lookup returned a row for this user.
   * False when the user is authenticated but not yet provisioned in the
   * ApplicationUsers table (admin needs to add them at `/admin/users`).
   */
  isProvisioned: boolean;
}

interface UseAuthResult {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isLoadingRoles: boolean;
  /**
   * True iff `/users/me` returned an explicit 404 — i.e. the user authenticated
   * with Azure AD but has no `ApplicationUser` row and no auto-provisioning
   * app role. The backend will 403 every protected endpoint for this user, so
   * the AuthGuard uses this to block them at the UI boundary instead of
   * dropping them on a dashboard that errors on every fetch. Distinct from
   * `!user.isProvisioned`, which is also true during transient network errors
   * and while the check is still in flight.
   */
  isUnauthorized: boolean;
  /**
   * True iff `/users/me` errored with anything other than 404 (network failure,
   * 401, 5xx, etc.). We can't confirm the user is authorized OR reject them;
   * the AuthGuard routes these to a recovery page rather than letting the
   * dashboard mount and 403 every request. Mutually exclusive with
   * `isUnauthorized`.
   */
  meCheckFailed: boolean;
  /** Forces `/users/me` to be re-fetched. Used by the recovery page Retry button. */
  retryMeCheck: () => void;
  login: () => Promise<void>;
  logout: () => Promise<void>;
}

// window.open returns null when popups are blocked — by a popup blocker, or
// always in embedded browsers (e.g. the Claude desktop app's browser pane) —
// and MSAL surfaces that as popup_window_error. MSAL has already released its
// interaction lock by then, so falling back to a full-page redirect is safe.
function isPopupBlocked(error: unknown): boolean {
  return (
    error instanceof BrowserAuthError &&
    error.errorCode === BrowserAuthErrorCodes.popupWindowError
  );
}

/**
 * Roles are loaded from the backend `/api/v1/users/me` endpoint rather than
 * decoded from the JWT in the browser. The backend already validates the
 * token and looks up the user by `AzureAdObjectId`, returning the role from
 * the `ApplicationUsers` row that admins manage at `/admin/users`. This is
 * the authoritative source.
 *
 * Why not decode the JWT in React?
 *   - Azure AD sometimes emits app roles only on the API-audience access
 *     token, not the SPA's ID token. If silent token acquisition fails and
 *     the popup fallback is blocked or dismissed, in-React decoding leaves
 *     roles permanently empty — that was the recurring "empty sidebar" bug.
 *   - Different tenant configurations emit the `roles` claim as either a
 *     string or an array. Defensive parsing missed edge cases.
 *   - The backend already has to resolve the role for authorization on every
 *     request, so a single `/me` query is the cheapest correct answer.
 */
export function useAuth(): UseAuthResult {
  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0] ?? undefined);
  const isAuthenticated = useIsAuthenticated();
  const queryClient = useQueryClient();

  const isLoading = inProgress !== InteractionStatus.None;

  const meQuery = useQuery({
    queryKey: usersKeys.me(),
    queryFn: usersApi.getCurrentUser,
    enabled: isAuthenticated && account !== null,
    staleTime: 5 * 60 * 1000,
    // Don't retry 404 (user not provisioned) or 401 (re-auth needed). The
    // apiClient response interceptor rejects with an `ApiError` shape rather
    // than a standard Error, so we cast via unknown to read its status.
    retry: (failureCount, error) => {
      const status = (error as unknown as ApiError | undefined)?.status;
      if (status === 401 || status === 404) return false;
      return failureCount < 1;
    },
  });

  const roles: UserRole[] = meQuery.data?.role ? [meQuery.data.role] : [];
  const isProvisioned = meQuery.isSuccess && meQuery.data !== undefined;
  const meErrorStatus = (meQuery.error as unknown as ApiError | undefined)?.status;
  const isUnauthorized =
    isAuthenticated && account !== null && meQuery.isError && meErrorStatus === 404;
  const meCheckFailed =
    isAuthenticated && account !== null && meQuery.isError && meErrorStatus !== 404;

  function retryMeCheck(): void {
    void queryClient.invalidateQueries({ queryKey: usersKeys.me() });
  }

  const user: AuthUser | null = account
    ? {
        id: account.localAccountId,
        email: account.username,
        name: account.name ?? account.username,
        roles,
        isProvisioned,
      }
    : null;

  async function login(): Promise<void> {
    try {
      await instance.loginPopup(loginRequest);
    } catch (error) {
      if (isPopupBlocked(error)) {
        await instance.loginRedirect(loginRequest);
        return;
      }
      console.error('Login failed:', error);
      throw error;
    }
  }

  async function logout(): Promise<void> {
    try {
      await instance.logoutPopup({
        postLogoutRedirectUri: '/',
        mainWindowRedirectUri: '/',
      });
    } catch (error) {
      if (isPopupBlocked(error)) {
        await instance.logoutRedirect({ postLogoutRedirectUri: '/' });
        return;
      }
      console.error('Logout failed:', error);
      throw error;
    }
  }

  return {
    user,
    isAuthenticated,
    isLoading,
    // Only treat as "loading roles" when the query is genuinely in-flight.
    // isSuccess/isError both end the loading state.
    isLoadingRoles:
      isAuthenticated && account !== null && meQuery.isPending && meQuery.fetchStatus !== 'idle',
    isUnauthorized,
    meCheckFailed,
    retryMeCheck,
    login,
    logout,
  };
}
